/**
 * <site-header> - dropdown menus (desktop) and the menu drawer (mobile).
 *
 * All links are server-rendered, so the menu works for crawlers and without JavaScript
 * (dropdowns also open on hover and the drawer's submenus are native <details>).
 * This script adds keyboard/touch toggles, Escape and click-outside closing.
 */
(function () {
  if (customElements.get('site-header')) return;

  const DESKTOP = window.matchMedia('(min-width: 990px)');

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.toggles = Array.from(this.querySelectorAll('.header-menu__toggle'));
      this.drawer = this.querySelector('#HeaderDrawer');
      this.drawerOpen = this.querySelector('[data-drawer-open]');

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);

      for (const toggle of this.toggles) {
        toggle.addEventListener('click', () => this.setExpanded(toggle, toggle.getAttribute('aria-expanded') !== 'true'));
        // A dropdown opened by click must not stay open next to one opened by hover.
        toggle.parentElement.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.closeAll(toggle);
        });
      }

      this.drawerOpen?.addEventListener('click', () => this.openDrawer());
      this.drawer?.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.drawer.close());
      this.drawer?.addEventListener('close', () => this.drawerOpen?.setAttribute('aria-expanded', 'false'));
      // Clicking the backdrop (the dialog element itself, outside its content) closes the drawer.
      this.drawer?.addEventListener('click', (event) => {
        if (event.target === this.drawer) this.drawer.close();
      });

      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);
      DESKTOP.addEventListener('change', this.onBreakpoint);

      // A hairline under the sticky header once the page scrolls beneath it. Only a class toggle,
      // so the header never changes height while scrolling.
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      DESKTOP.removeEventListener('change', this.onBreakpoint);
      window.removeEventListener('scroll', this.onScroll);
    }

    setExpanded(toggle, expanded) {
      if (expanded) this.closeAll(toggle);
      toggle.setAttribute('aria-expanded', String(expanded));
    }

    closeAll(except) {
      for (const toggle of this.toggles) {
        if (toggle !== except) toggle.setAttribute('aria-expanded', 'false');
      }
    }

    openDrawer() {
      if (!this.drawer) return;
      this.drawer.showModal();
      this.drawerOpen?.setAttribute('aria-expanded', 'true');
    }

    onDocumentClick(event) {
      if (!event.target.closest('.header-menu__item--dropdown')) this.closeAll();
      for (const details of this.querySelectorAll('.site-header__actions details[open]')) {
        if (!details.contains(event.target)) details.open = false;
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const open = this.toggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true');
      if (open) {
        open.setAttribute('aria-expanded', 'false');
        open.focus();
        return;
      }
      const details = event.target.closest('details[open]');
      if (details && !details.classList.contains('drawer-menu__details')) {
        details.open = false;
        details.querySelector('summary')?.focus();
      }
    }

    onBreakpoint() {
      if (DESKTOP.matches && this.drawer?.open) this.drawer.close();
      this.closeAll();
    }
  }

  customElements.define('site-header', SiteHeader);
})();
